// src/components/dashboard/AddProduct.jsx  (ProductForm)
import React, { useEffect, useState } from "react";
import { AddIcon } from "../../assets/icons/icons";
import axios from "axios";
import CustomAlert from "../CustomAlert";

/* كابستي للجميع + إضافة صفر كابستي */
const capacityList = [
  "0 GB", // مضافة
  "8 GB",
  "16 GB",
  "32 GB",
  "64 GB",
  "128 GB",
  "256 GB",
  "512 GB",
  "1 T",
];

/* always show capacity */
const shouldShowCapacity = () => true;

/** normalize (أقوى: يحوّل & إلى and ويزيل المسافات والشرطات والشرطات السفلية والشرطات المائلة والنقاط) */
// eslint-disable-next-line no-unused-vars
const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[\s_\-/.]+/g, "");

/* تعقيم كود اللون (#RGB/#RRGGBB) */
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`;
  return "";
};

function ProductForm() {
  const [mainImage, setMainImage] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // ألوان ديناميكية (اسم + hex)
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#000000");
  const [colorsWithHex, setColorsWithHex] = useState([]); // [{name, hex}]

  // أسعار السعات (key = "128 GB", value = السعر)
  const [capacityPrices, setCapacityPrices] = useState({ "0 GB": "" });

  const [formData, setFormData] = useState({
    product_name_E: "",
    product_name_A: "",
    product_tag: "",
    product_category: "",
    product_sub_category: "",
    product_brand_name: "",
    product_price_discount: "",
    product_stock_available: "",
    product_desc_E: "",
    product_desc_A: "",
    product_features_A: "",
    product_features_E: "",
    product_color: [],          // أسماء الألوان فقط
    product_capacity: ["0 GB"], // نبدأ مختار "0 GB" افتراضيًا
    product_status: "",
  });

  const token = sessionStorage.getItem("authToken");

  /* Attach token once */
  useEffect(() => {
    if (!token) {
      setErrors({ general: "Authentication token is missing. Please log in." });
      setLoading(false);
    } else {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  /* Fetch selects data */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, brandRes, subRes] = await Promise.all([
          axios.get("https://laravel-api.aj-mart.com/api/categories"),
          axios.get("https://laravel-api.aj-mart.com/api/brand"),
          axios.get("https://laravel-api.aj-mart.com/api/subCategory"),
        ]);

        const fetchedCategories = Array.isArray(catRes.data.category) ? catRes.data.category : [];
        const uniqueCategories = Array.from(
          new Map(fetchedCategories.map((i) => [i.category_name_E?.toLowerCase(), i])).values()
        ).filter((i) => i && i.category_name_E);

        const fetchedBrands = Array.isArray(brandRes.data.brands) ? brandRes.data.brands : [];
        const uniqueBrands = Array.from(
          new Map(fetchedBrands.map((i) => [i.brand_name?.toLowerCase(), i])).values()
        ).filter((i) => i && i.brand_name);

        const fetchedSubCategories = Array.isArray(subRes.data.sub_category) ? subRes.data.sub_category : [];
        const uniqueSubCategories = Array.from(
          new Map(fetchedSubCategories.map((i) => [i.sub_category_name_E?.toLowerCase(), i])).values()
        ).filter((i) => i && i.sub_category_name_E);

        setCategories(uniqueCategories);
        setBrands(uniqueBrands);
        setSubCategories(uniqueSubCategories);
      } catch (e) {
        console.error("Error fetching data:", e);
        setErrors({ general: "Failed to load categories, brands, or subcategories. Please try again." });
        setCategories([]);
        setBrands([]);
        setSubCategories([]);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token]);

  /* Image helpers */
  const validateImage = (file) => {
    const maxSizeMB = 5;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) return "Only JPEG, PNG, or GIF images are allowed.";
    if (file.size > maxSizeMB * 1024 * 1024) return `Image size must be less than ${maxSizeMB}MB.`;
    return null;
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setErrors((p) => ({ ...p, product_main_img: err }));
      return;
    }
    setMainImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setMainPreview(reader.result);
    reader.readAsDataURL(file);
    setErrors((p) => ({ ...p, product_main_img: undefined }));
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    const errs = files.map(validateImage).filter(Boolean);
    if (errs.length) {
      setErrors((p) => ({ ...p, product_images: errs[0] }));
      return;
    }
    setAdditionalImages(files);
    Promise.all(
      files.map(
        (f) =>
          new Promise((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.readAsDataURL(f);
          })
      )
    ).then((previews) => setAdditionalPreviews(previews));
    setErrors((p) => ({ ...p, product_images: undefined }));
  };

  /* Form changes */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  /* ===== ألوان ديناميكية ===== */
  const addColor = () => {
    const name = colorName.trim();
    if (!name) {
      setErrors((p) => ({ ...p, product_color: "Please enter a color name." }));
      return;
    }
    const exists = formData.product_color.some((c) => c.toLowerCase() === name.toLowerCase());
    if (exists) {
      setErrors((p) => ({ ...p, product_color: "This color name is already added." }));
      return;
    }
    const hex = sanitizeCssColor(colorHex) || ""; // hex اختياري
    setFormData((p) => ({ ...p, product_color: [...p.product_color, name] }));
    setColorsWithHex((prev) => [...prev, { name, hex }]);
    setColorName("");
    // لا نمسح اللون المختار من الـ picker بعد الإضافة (مفيد للإضافات المتتالية)
    if (errors.product_color) setErrors((p) => ({ ...p, product_color: undefined }));
  };

  const removeColor = (name) => {
    setFormData((p) => ({
      ...p,
      product_color: p.product_color.filter((c) => c.toLowerCase() !== name.toLowerCase()),
    }));
    setColorsWithHex((prev) =>
      prev.filter((c) => c.name.toLowerCase() !== name.toLowerCase())
    );
  };

  const updateColorHex = (name, hexVal) => {
    const sanitized = sanitizeCssColor(hexVal) || "";
    setColorsWithHex((prev) =>
      prev.map((c) =>
        c.name.toLowerCase() === name.toLowerCase() ? { ...c, hex: sanitized } : c
      )
    );
  };

  const handleColorNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addColor();
    }
  };

  // سعات + إبقاء prices متزامنة
  const handleCapacityCheck = (e) => {
    const val = e.target.value;
    setFormData((p) => {
      let selected = p.product_capacity.includes(val)
        ? p.product_capacity.filter((c) => c !== val)
        : [...p.product_capacity, val];

      // لا تتركه فاضيًا — ضيف "0 GB" تلقائيًا إذا صار فاضي
      if (selected.length === 0) selected = ["0 GB"];

      setCapacityPrices((old) => {
        const next = { ...old };
        // ضيف مدخل سعر للسعة الجديدة إن لم يوجد
        selected.forEach((cap) => {
          if (!(cap in next)) next[cap] = "";
        });
        // نظّف أي أسعار لسعات لم تعد مختارة
        Object.keys(next).forEach((k) => {
          if (!selected.includes(k)) delete next[k];
        });
        return next;
      });

      return { ...p, product_capacity: selected };
    });
    if (errors.product_capacity) setErrors((p) => ({ ...p, product_capacity: undefined }));
  };

  const handleCapacityPriceChange = (cap, v) => {
    setCapacityPrices((p) => ({ ...p, [cap]: v }));
  };

  /* Reset */
  const handleReset = () => {
    setFormData({
      product_name_E: "",
      product_name_A: "",
      product_tag: "",
      product_category: "",
      product_sub_category: "",
      product_brand_name: "",
      product_price_discount: "",
      product_stock_available: "",
      product_desc_E: "",
      product_desc_A: "",
      product_features_A: "",
      product_features_E: "",
      product_color: [],
      product_capacity: ["0 GB"], // ارجاع صفر كابستي
      product_status: "",
    });
    setCapacityPrices({ "0 GB": "" }); // ارجاع سعر صفر كابستي
    setMainImage(null);
    setMainPreview(null);
    setAdditionalImages([]);
    setAdditionalPreviews([]);
    setColorsWithHex([]);
    setColorName("");
    setColorHex("#000000");
    setErrors({});
  };

  /* Auto-dismiss لرسالة النجاح بعد 5 ثوانٍ */
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(""), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  /* Render small error */
  const renderError = (name) =>
    errors[name] ? (
      <p className="text-red-500 text-sm mt-1">
        {Array.isArray(errors[name]) ? errors[name][0] : errors[name]}
      </p>
    ) : null;

  // ===== Helpers للإرسال بصيغة PHP Arrays =====
  const buildFormData = ({ formData, capacityPrices, mainImage, additionalImages }) => {
    const fd = new FormData();

    // تأكد دائمًا من وجود "0 GB" ضمن القائمة قبل الإرسال
    const selectedCaps =
      Array.isArray(formData.product_capacity) && formData.product_capacity.length
        ? Array.from(new Set([...formData.product_capacity, "0 GB"]))
        : ["0 GB"];

    // 1) حقول نصية عادية
    const simple = {
      product_name_E: formData.product_name_E,
      product_name_A: formData.product_name_A,
      product_tag: formData.product_tag,
      product_category: formData.product_category,
      product_sub_category: formData.product_sub_category,
      product_brand_name: formData.product_brand_name,
      product_price_discount: formData.product_price_discount,
      product_stock_available: formData.product_stock_available,
      product_desc_E: formData.product_desc_E,
      product_desc_A: formData.product_desc_A,
      product_features_A: formData.product_features_A,
      product_features_E: formData.product_features_E,
      product_status: formData.product_status,
    };
    Object.entries(simple).forEach(([k, v]) => fd.append(k, v ?? ""));

    // 2) السعر الأساسي = أقل سعر من أسعار السعات (بما فيها 0 GB)
    const capNums = Object.entries(capacityPrices)
      .filter(([cap]) => selectedCaps.includes(cap))
      .map(([, v]) => Number.parseFloat(String(v).replace(",", ".")))
      .filter((n) => Number.isFinite(n) && n >= 0);

    const inferredBasePrice = capNums.length ? Math.min(...capNums) : "";
    fd.append("product_price", inferredBasePrice !== "" ? String(inferredBasePrice) : "");

    // 3) ألوان كمصفوفة PHP: product_color[]
    (formData.product_color || []).forEach((c) => fd.append("product_color[]", c));

    // 3.1) ألوان مع HEX كـ JSON (لو السيرفر يدعمها)
    // نولّدها بناءً على أسماء الألوان المختارة لضمان الترتيب والتطابق
    const jsonColors = (formData.product_color || []).map((name) => {
      const found = colorsWithHex.find((c) => c.name.toLowerCase() === name.toLowerCase());
      return { name, hex: sanitizeCssColor(found?.hex || "") };
    });
    fd.append("product_colors_with_hex", JSON.stringify(jsonColors));

    // 4) سعات + أسعار السعات بطريقة PHP/Laravel — دائمًا
    selectedCaps.forEach((cap) => fd.append("product_capacity[]", cap));
    Object.entries(capacityPrices).forEach(([cap, price]) => {
      if (selectedCaps.includes(cap) && price !== "" && price != null) {
        fd.append(`product_capacity_prices[${cap}]`, String(price));
      }
    });

    // 5) الصور
    if (mainImage) fd.append("product_main_img", mainImage);
    (additionalImages || []).forEach((img) => fd.append("product_images[]", img));

    return fd;
  };

  /* Submit */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.product_name_E) newErrors.product_name_E = "Product name in English is required.";
    if (!formData.product_name_A) newErrors.product_name_A = "Product name in Arabic is required.";
    if (!formData.product_category) newErrors.product_category = "Product category is required.";
    if (!formData.product_sub_category) newErrors.product_sub_category = "Product sub category is required.";
    if (!formData.product_brand_name) newErrors.product_brand_name = "Product brand is required.";
    if (!formData.product_stock_available) newErrors.product_stock_available = "Stock available is required.";
    if (!formData.product_status) newErrors.product_status = "Product status is required.";
    if (!mainImage) newErrors.product_main_img = "Main image is required.";

    // بما أن الكابستي للجميع، لازم يكون في سعر لسعة واحدة على الأقل
    const hasAnyCapPrice = Object.values(capacityPrices).some(
      (v) => v !== "" && v != null && Number.isFinite(Number(v))
    );
    if (!hasAnyCapPrice) {
      newErrors.product_price = "Please set at least one capacity price (e.g. for 0 GB).";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage("");

    const fd = buildFormData({
      formData,
      capacityPrices,
      mainImage,
      additionalImages,
    });

    try {
      const { data, status } = await axios.post(
        "https://laravel-api.aj-mart.com/api/products/add",
        fd,
        { headers: { Accept: "application/json" } }
      );

      if (status >= 200 && status < 300) {
        setSuccessMessage(data?.message || "تمت إضافة المنتج بنجاح");
        handleReset();
        window.scrollTo(0, 0);
      } else {
        setErrors({ general: data?.message || "Something went wrong" });
      }
    } catch (err) {
      console.error("API ERROR:", {
        status: err?.response?.status,
        data: err?.response?.data,
      });

      if (err?.response?.status === 422) {
        setErrors(err.response.data?.errors || { general: err.response.data?.message || "Validation error" });
      } else if (err?.response?.status === 401) {
        setErrors({ general: "Unauthorized. Please log in again." });
      } else {
        setErrors({
          general:
            err?.response?.data?.message ||
            `Server error (${err?.response?.status || 500}). Please try again.`,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <p className="text-center text-gray-500">Loading categories...</p>;

  if (errors.general && !token) {
    return (
      <div className="text-center">
        <CustomAlert
          type="error"
          message={errors.general}
          onClose={() => setErrors((p) => ({ ...p, general: "" }))}
        />
      </div>
    );
  }

  return (
    <section className="py-6 flex flex-col gap-8">
      <div className="text-2xl font-bold text-[#106c89]">Add Product</div>

      <div className="w-full space-y-4">
        {successMessage && (
          <CustomAlert
            type="success"
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errors.general && (
          <CustomAlert
            type="error"
            message={errors.general}
            onClose={() => setErrors((p) => ({ ...p, general: "" }))}
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-6" encType="multipart/form-data">
          {/* --------- Basic Info --------- */}
          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="text-lg font-semibold text-gray-800">Basic Information</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Product Name EN */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Product Name In English</label>
                <input
                  type="text"
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_name_E ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Product Name In English"
                  name="product_name_E"
                  value={formData.product_name_E}
                  onChange={handleChange}
                  required
                />
                {renderError("product_name_E")}
              </div>

              {/* Product Name AR */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Product Name In Arabic</label>
                <input
                  type="text"
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_name_A ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Product Name In Arabic"
                  name="product_name_A"
                  value={formData.product_name_A}
                  onChange={handleChange}
                  required
                />
                {renderError("product_name_A")}
              </div>

              {/* Tags */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Tags</label>
                <input
                  type="text"
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_tag ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Tags (comma-separated)"
                  name="product_tag"
                  value={formData.product_tag}
                  onChange={handleChange}
                />
                {renderError("product_tag")}
              </div>

              {/* Discount */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Discount (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_price_discount ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Discount percentage"
                  name="product_price_discount"
                  value={formData.product_price_discount}
                  onChange={handleChange}
                />
                {renderError("product_price_discount")}
              </div>

              {/* Category */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Select Product Category</label>
                <select
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_category ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  name="product_category"
                  value={formData.product_category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Product Category</option>
                  {categories.length ? (
                    categories.map((c) => (
                      <option key={c.category_id} value={c.category_name_E}>
                        {c.category_name_E}
                      </option>
                    ))
                  ) : (
                    <option value="">No categories available</option>
                  )}
                </select>
                {renderError("product_category")}
              </div>

              {/* Sub Category */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Select Product Sub Category</label>
                <select
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_sub_category ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  name="product_sub_category"
                  value={formData.product_sub_category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Product Sub Category</option>
                  {subCategories.length ? (
                    subCategories.map((s) => (
                      <option key={s.sub_category_id} value={s.sub_category_name_E}>
                        {s.sub_category_name_E}
                      </option>
                    ))
                  ) : (
                    <option value="">No sub categories available</option>
                  )}
                </select>
                {renderError("product_sub_category")}
              </div>

              {/* Brand */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Select Brand Name</label>
                <select
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_brand_name ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  name="product_brand_name"
                  value={formData.product_brand_name}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Product Brand</option>
                  {brands.length ? (
                    brands.map((b) => (
                      <option key={b.brand_id} value={b.brand_name}>
                        {b.brand_name}
                      </option>
                    ))
                  ) : (
                    <option value="">No brands available</option>
                  )}
                </select>
                {renderError("product_brand_name")}
              </div>

              {/* Stock */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Stock Available</label>
                <input
                  type="number"
                  min="0"
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_stock_available ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Stock available"
                  name="product_stock_available"
                  value={formData.product_stock_available}
                  onChange={handleChange}
                  required
                />
                {renderError("product_stock_available")}
              </div>

              {/* Status */}
              <div className="flex flex-col">
                <label className="text-black capitalize">Product Status</label>
                <select
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] ${
                    errors.product_status ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  name="product_status"
                  value={formData.product_status}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Product Status</option>
                  <option value="New">New</option>
                  <option value="Used">Used</option>
                </select>
                {renderError("product_status")}
              </div>
            </div>
          </div>

          {/* --------- Descriptions/Features --------- */}
          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="text-lg font-semibold text-gray-800">Descriptions & Features</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Features EN */}
              <div className="flex flex-col">
                <label className="text-black">
                  Features of Product In English "(Write \n to new Line)"
                </label>
                <textarea
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] h-[250px] max-h-[300px] ${
                    errors.product_features_E ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Features of product In English"
                  name="product_features_E"
                  value={formData.product_features_E}
                  onChange={handleChange}
                />
                {renderError("product_features_E")}
              </div>

              {/* Features AR */}
              <div className="flex flex-col">
                <label className="text-black">
                  Features of Product In Arabic "(Write \ة to new Line)"
                </label>
                <textarea
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] h-[250px] max-h-[300px] ${
                    errors.product_features_A ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Features of product In Arabic"
                  name="product_features_A"
                  value={formData.product_features_A}
                  onChange={handleChange}
                />
                {renderError("product_features_A")}
              </div>

              {/* Desc EN */}
              <div className="flex flex-col">
                <label className="text-black">
                  Description of Product In English "(Write \n to new Line)"
                </label>
                <textarea
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] h-[250px] max-h-[300px] ${
                    errors.product_desc_E ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Description of product In English"
                  name="product_desc_E"
                  value={formData.product_desc_E}
                  onChange={handleChange}
                />
                {renderError("product_desc_E")}
              </div>

              {/* Desc AR */}
              <div className="flex flex-col">
                <label className="text-black">
                  Description of Product In Arabic "(Write \ة to new Line)"
                </label>
                <textarea
                  className={`px-3 py-2 border-2 rounded text-black bg-[#eeeeee6e] h-[250px] max-h-[300px] ${
                    errors.product_desc_A ? "border-red-500" : "border-[#1485a6]"
                  }`}
                  placeholder="Description of product In Arabic"
                  name="product_desc_A"
                  value={formData.product_desc_A}
                  onChange={handleChange}
                />
                {renderError("product_desc_A")}
              </div>
            </div>
          </div>

          {/* --------- Colors & Capacity --------- */}
          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="text-lg font-semibold text-gray-800">Options</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Colors (Dynamic) */}
              <div className="flex flex-col gap-2">
                <label className="text-black capitalize">Colors</label>

                <div className="flex flex-col gap-3 p-4 bg-gray-100 border-2 rounded border-[#1485a6]">
                  <div className="flex flex-wrap gap-3 items-center">
                    <input
                      type="text"
                      placeholder="Color name (e.g. Midnight Blue)"
                      value={colorName}
                      onChange={(e) => setColorName(e.target.value)}
                      onKeyDown={handleColorNameKeyDown}
                      className="flex-1 min-w-[220px] px-3 py-2 border rounded"
                    />
                    <input
                      type="color"
                      title="Color HEX (optional)"
                      value={colorHex}
                      onChange={(e) => setColorHex(e.target.value)}
                      className="w-[48px] h-[40px] border rounded cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={addColor}
                      className="px-3 py-2 bg-[#1485a6] text-white rounded hover:opacity-90"
                    >
                      Add Color
                    </button>
                  </div>

                  {/* chips */}
                  {formData.product_color.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.product_color.map((name) => {
                        const obj = colorsWithHex.find(
                          (c) => c.name.toLowerCase() === name.toLowerCase()
                        ) || { name, hex: "" };
                        return (
                          <div
                            key={name}
                            className="flex items-center gap-2 bg-white border rounded-full px-3 py-1 shadow-sm"
                          >
                            <span
                              className="inline-block w-4 h-4 rounded-full border"
                              style={{ backgroundColor: obj.hex || "transparent" }}
                              title={obj.hex || "No HEX"}
                            />
                            <span className="text-sm font-medium">{name}</span>
                            <input
                              type="color"
                              value={obj.hex || "#000000"}
                              onChange={(e) => updateColorHex(name, e.target.value)}
                              className="w-[32px] h-[24px] border rounded cursor-pointer"
                              title="Change HEX"
                            />
                            <button
                              type="button"
                              onClick={() => removeColor(name)}
                              className="text-red-600 hover:text-red-700 text-sm font-semibold"
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No colors added yet.</p>
                  )}
                </div>
                {renderError("product_color")}
              </div>

              {/* Capacity — للجميع + فيها "0 GB" */}
              {shouldShowCapacity(formData.product_category) && (
                <div className="flex flex-col gap-2">
                  <label className="text-black capitalize">Select Capacity</label>
                  <div className="p-4 bg-gray-100 border-2 rounded border-[#1485a6]">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {capacityList.map((cap) => (
                        <label key={cap} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className={`w-[18px] h-[18px] accent-blue-500 border-gray-300 rounded focus:ring-2 focus:ring-blue-300 ${
                              errors.product_capacity ? "border-red-500" : ""
                            }`}
                            value={cap}
                            checked={formData.product_capacity.includes(cap)}
                            onChange={handleCapacityCheck}
                          />
                          <span className="text-gray-700 font-medium">{cap}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {renderError("product_capacity")}

                  {/* Capacity prices */}
                  {formData.product_capacity.length > 0 && (
                    <div className="mt-3 p-4 bg-white border rounded">
                      <div className="text-sm font-semibold mb-2">Per-capacity price</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {formData.product_capacity.map((cap) => (
                          <div key={cap} className="flex items-center gap-2">
                            <label className="min-w-[90px] text-sm">{cap}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={capacityPrices[cap] ?? ""}
                              onChange={(e) => handleCapacityPriceChange(cap, e.target.value)}
                              className="flex-1 px-3 py-2 border rounded"
                              placeholder="Override price (e.g. 1350)"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        إذا تركت سعر بعض السعات فاضي، السيرفر يعتمد السعر الأساسي. يفضّل تعبئة سعر
                        <span className="mx-1 font-semibold">0 GB</span> كسعر عادي.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* --------- Images --------- */}
          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="text-lg font-semibold text-gray-800">Images</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Main */}
              <div className="flex flex-col items-center">
                <label
                  htmlFor="main-image-upload"
                  className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] text-black rounded flex items-center justify-center relative overflow-hidden"
                >
                  {mainPreview ? (
                    <img src={mainPreview} alt="Main Preview" className="w-full h-full object-cover" />
                  ) : (
                    <span>Main Image</span>
                  )}
                </label>
                <input
                  id="main-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="hidden"
                  onChange={handleMainImageChange}
                  required
                />
                {renderError("product_main_img")}
              </div>

              {/* Additional */}
              <div className="flex flex-col items-center">
                <label
                  htmlFor="additional-images-upload"
                  className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] text-black rounded flex items-center justify-center"
                >
                  {additionalPreviews.length ? (
                    <div className="grid grid-cols-2 gap-2 w-full h-full p-2 overflow-y-auto">
                      {additionalPreviews.map((src, i) => (
                        <img key={i} src={src} alt={`Preview ${i + 1}`} className="w-full h-[80px] object-cover rounded" />
                      ))}
                    </div>
                  ) : (
                    <span>Upload Additional Images</span>
                  )}
                </label>
                <input
                  id="additional-images-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  multiple
                  className="hidden"
                  onChange={handleAdditionalImagesChange}
                />
                {renderError("product_images")}
              </div>
            </div>
          </div>

          {/* --------- Submit --------- */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-[#1485a6] text-white px-4 py-2 rounded hover:bg-[#106c89] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AddIcon />
              <span>{isSubmitting ? "Adding..." : "Add Product"}</span>
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default ProductForm;
